import { LitElement, css, html, unsafeCSS } from 'lit';
import { customElement, property, query, state } from 'lit/decorators';
import { getFromLocalStorage, isDefined, isEmpty, undefinedIfEmpty } from '../../helpers/utils';
import { getMainPage, getPageNameByType } from '../../helpers/page-name';
import { getNewUiUrl } from '../../helpers/auth-cookie';

import { Dialog } from '@material/mwc-dialog';
import {
    FeatureType,
} from '../../types';
import { SettingDrawer } from '../mobile/setting-drawer';
import { StateController } from '../../helpers/state/state-management/state-controller';
import accountInfo from '../../helpers/state/account-info';
import { detectMobileViewport } from '../../helpers/device';
import { heights } from '../../style/variables';
import { ifDefined } from 'lit/directives/if-defined';
import { logWithAmplitude } from '../../helpers/api/api';
import { openChargebeePortal } from '../../helpers/chargebee';
import organizationInfo from '../../helpers/state/organization-info';
import { redirectToLogin } from '../../helpers/api/account-api';
import { renderOrganizationLogo } from '../../helpers/files';
import subscriptionInfo from '../../helpers/state/subscription-info';
import topicData from '../../helpers/state/topic-data';

const pagesForHelp = [
    'admin-portal',
    'backpack',
    'account',
];

@customElement('nav-bar')
export class Navbar extends LitElement {
    static amplitude_callsite: string = 'Nav Bar';



    @query('#duplicationModal')
    duplicationModal?: Dialog;

    @query('#duplicateDrawer')
    duplicateDrawer?: SettingDrawer;

    @query('#shareModal')
    shareModal?: Dialog;

    @query('#upgradeDialogComponent')
    upgradeDialogComponent!: Dialog;

    @query('#signinModalComponent')
    signinModalComponent?: Dialog;

    @property({ type: String })
    crowdsourceLink?: string;

    @property({ type: String })
    viewonlyLink?: string;

    @property()
    type?: string = 'admin-portal';

    @property()
    subscription_level_text?: string;

    @state()
    loading: boolean = false;

    private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindAccountState = new StateController(this, accountInfo);

    async connectedCallback() {
        await this.startup();
        super.connectedCallback();
    }

    private async startup() {
        if (topicData.topicKey === undefined && this.type !== 'discover' && !accountInfo.isAuthenticated) {
            redirectToLogin();
        }

        // get subscription level and set text as subscription level
        if (accountInfo.isAuthenticated) {
            // if organization is defined then set subscription level text to "Teams"
            if (subscriptionInfo.subscriptionLevelText === 'Free' && !organizationInfo.isGuest) {
                this.subscription_level_text = 'Subscribe';
            }  else {
                this.subscription_level_text = subscriptionInfo.subscriptionLevelText;
            }
        }
    }

    private async closeOpenDrawers() {
        await this.duplicateDrawer?.close();
    }

    private pricingClick() {
        if (!organizationInfo.isGuest) {
            logWithAmplitude(Navbar.amplitude_callsite, 'Proxi Pricing Click');
            window.location.assign('/#home?page_name=Team%20Settings&setting_type=SubscriptionAndBilling');
        }
    }

    private whatsNewClick() {
        logWithAmplitude(Navbar.amplitude_callsite, 'What\'s New Click');
        window.open('https://proxi.sleekplan.app/changelog', '_blank');
    }

    private marketingAndPrintingServicesClick() {
        logWithAmplitude(Navbar.amplitude_callsite, 'Marketing and Printing Services Click');
        window.open('https://www.proxi.co/proxi-map-services');
    }

    private openAccountPage() {
        logWithAmplitude(Navbar.amplitude_callsite, 'Account Page Click');
        window.location.assign(`/#account`);
    }

    private async openChargebeePortal() {
        logWithAmplitude(Navbar.amplitude_callsite, 'Manage Subscription Click');
        if (!isDefined(subscriptionInfo.subscription_customer_id)) {
            getMainPage().errorToastElement?.showToastAndThrow('An Error Occured - Unable to open subscription portal. Reach out to help.proxi.co');
        } else {
            const request = {
                customer_id: subscriptionInfo.subscription_customer_id,
                redirect_url: window.location.href ?? 'https://map.proxi.co/#home',
            };
            await openChargebeePortal(request);
        }
    }


    protected render() {
        const logo = renderOrganizationLogo(organizationInfo.organization?.logo);
        return html`
            <header class="wrapper">
                <nav class="desktopOnly flexContainer">
                    <div id="leftContent" class="flexContainer">
                        <div class="flexContainer">
                            ${accountInfo.isAuthenticated
                                ? html` ${this.type === 'admin-portal'
                                      ? html`
                                            <form-button variant="navbutton"
                                                size="xs"
                                                class="desktopOnly"
                                                @click="${this.proxiBackpackClick}">
                                                    <img
                                                        src="/images/backpack_icon.png"
                                                        class="backpackicon"
                                                    />
                                                Back to Dashboard
                                            </form-button>
                                            `
                                      : html`
                                            <div class="divider"></div>
                                            ${this.type !== 'backpack'
                                                ? html`
                                                <form-button variant="navbutton"
                                                    size="xs"
                                                    class="desktopOnly"
                                                    @click="${this.proxiBackpackClick}">
                                                        <img
                                                            src="/images/backpack_icon.png"
                                                            class="backpackicon"
                                                        />
                                                    Your Maps
                                                </form-button>`
                                                : ``}
                                            ${this.type !== 'discover' && isEmpty(organizationInfo.organization)
                                                ? html`
                                                <form-button variant="navbutton"
                                                    size="xs"
                                                    @click="${this.discoverClick}">
                                                        <mwc-icon class="leftIcon">search</mwc-icon>
                                                        Discover
                                                </form-button>`
                                                : ``
                                            }
                                        `}`
                                : html` <div class="flexContainer">
                                      <div
                                          class="linkButton flexContainer"
                                          @click="${this.signInClick}"
                                      >
                                          <mwc-icon class="leftIcon">person_outline</mwc-icon>
                                          <span class="linkText">Log in to save and manage your maps</span>
                                      </div>
                                  </div>`}
                                ${pagesForHelp.includes(this.type as string)
                                    ? html`
                                        <help-menu
                                            type="${this.type as string}">
                                        </help-menu>
                                        <form-button variant="navbutton"
                                            size="xs"
                                            @click="${this.demoClick}">
                                                <mwc-icon class="leftIcon">ondemand_video</mwc-icon>
                                                Demo Video
                                        </form-button>
                                        `

                                    : ``}
                        </div>
                    </div>
                    <div
                        id="rightContent"
                        class="flexContainer"
                        style="padding-right: ${this.type === 'admin-portal' ? '20px' : '30px'}"
                    >
                        ${this.type === 'admin-portal'
                            ? ''
                            : html`
                                <form-button
                                    size="xs"
                                    class="buttonSection"
                                    @click="${this.startMapClick}"
                                >
                                    <mwc-icon class="shareButtonIcon ${this.loading ? 'spin' : ''}">add</mwc-icon>
                                    New Map or Experience
                                </form-button>
                                <div class="divider"></div>
                            `
                        }
                        ${accountInfo.isAuthenticated ? html`
                            <div class="flexContainer">
                                ${accountInfo.isAppSumoUser === true ? html`
                                    <div class="subscriptionTag" @click=${this.accountPageClick}>
                                        <div class="subscriptionNameTag">${this.subscription_level_text}</div>
                                    </div>
                                ` : html`
                                    <div class="subscriptionTag" @click=${this.pricingClick}>
                                        <div class="subscriptionNameTag">${this.subscription_level_text}</div>
                                    </div>
                                `}
                            </div>
                        <context-menu
                            class="${this.type === 'admin-portal' ? 'desktopOnly' : ''}"
                            type=${ifDefined(this.type)}
                            amplitude_callsite=${Navbar.amplitude_callsite}
                            @on-manage-subscription=${this.openChargebeePortal}
                        >
                        </context-menu>` : html``}
                    </div>
                </nav>
                <nav class="mobileOnly flexContainer">
                    <div id="leftContent" class="flexContainer">
                        ${this.type === 'admin-portal' ? html`
                            <a href="/#home" class="homeLink"><mwc-icon>home</mwc-icon></a>
                        ` : logo}
                        <div class="mobileStack">
                            <p class="pageName">${getPageNameByType(this.type)}</p>
                            ${accountInfo.isAuthenticated ? html`
                                <div class="flexContainer">
                                    <div class="subscriptionTag" @click=${this.pricingClick}>
                                        <div class="subscriptionNameTag">${this.subscription_level_text}</div>
                                    </div>
                                </div>`
                            : ''}
                        </div>
                    </div>
                    <div
                        id="rightContent"
                        class="flexContainer"
                    >
                        ${this.type === 'admin-portal' && detectMobileViewport() // show mobile only
                            ? html`
                                <div class="flexContainer" id="shareSection">
                                    <form-button
                                        size="sm"
                                        style="margin-left: 0.5rem"
                                        @click="${this.shareMapClick}"
                                    >
                                        Share <br> Map
                                    </form-button>
                                </div>`
                            : html`
                                <form-button
                                    size="xs"
                                    class="buttonSection"
                                    @click="${this.startMapClick}"
                                >
                                    <mwc-icon class="shareButtonIcon ${this.loading ? 'spin' : ''}">add</mwc-icon>
                                    New Map or Experience
                                </form-button>
                            `
                        }
                        ${accountInfo.isAuthenticated ? html`
                        <context-menu
                            type=${ifDefined(this.type)}
                            style="margin-left: 0.5rem"
                            amplitude_callsite=${Navbar.amplitude_callsite}
                            @on-manage-subscription=${this.openChargebeePortal}
                            >
                        </context-menu> ` : html``}
                    </div>
                </nav>

                ${detectMobileViewport() ?
                    html`<setting-drawer id="duplicateDrawer">
                        <duplicate-map-component
                            .topic=${topicData.topic}
                            @on-topic-duplicated="${async () => await this.duplicateDrawer?.close()}"
                            @close-upgrade="${() => this.duplicationModal?.close()}">
                        </duplicate-map-component>
                        </setting-drawer>` :
                    html`<mwc-dialog id="duplicationModal" class="dialog" hideActions>
                            <modal-dialog size="md" @close="${() => this.duplicationModal?.close()}">
                                <duplicate-map-component
                                    .topic=${topicData.topic}
                                    slot="body"
                                    @on-topic-duplicated="${async () => await this.duplicateDrawer?.close()}"
                                    @close-upgrade="${() => this.duplicationModal?.close()}">
                                </duplicate-map-component>
                            </modal-dialog>
                        </mwc-dialog>`
                }

                <mwc-dialog id="signinModalComponent" class="dialog" hideActions>
                    <modal-dialog size="md" @close="${() => this.signinModalComponent?.close()}">
                        <signin-modal-component
                            slot="body"
                            emailAddress=${getFromLocalStorage('email') ?? ''}
                            @close="${() => this.signinModalComponent?.close()}">
                        </signin-modal-component>
                    </modal-dialog>
                </mwc-dialog>

                <mwc-dialog id="upgradeDialogComponent" class="dialog" hideActions>
                    <modal-dialog size="md" @close="${() => this.upgradeDialogComponent.close()}">
                        <upgrade-dialog
                            slot="body"
                            .featureType="${FeatureType.PrintableMap}"
                            @close="${() => this.upgradeDialogComponent.close()}"
                        >
                        </upgrade-dialog>
                    </modal-dialog>
                </mwc-dialog>

                <error-toast id="errorToast"></error-toast>
            </header>
        `;
    }

    static get styles() {
        return css`
            .wrapper {
                postion: fixed;
                width: 100%;
            }

            :host {
                --mdc-menu-min-width: min(100vw, 292px);
                --mdc-typography-subtitle1-font-size: 12px;
            }

            mwc-dialog {
                /* this is because handontable sets the zindex of its columns and row headers to 160 */
                z-index: 200;
            }

            nav {
                height: ${unsafeCSS(heights.nav)};
                display: flex;
                align-items: center;
                justify-content: space-between;
                box-shadow: 0px 4px 6px 0 #ECECEC;
                z-index: 2;
                padding-left: 1rem;
                padding-right: 1rem;
            }

            .shareButtonIcon {
                --mdc-icon-size: 1.5rem;
            }

            .no-pointer:hover {
                cursor: default;
            }

            #logoDesktop img {
                max-height: 2.5rem;
                vertical-align: middle;
            }

            #logoMobile img {
                max-height: 1.5rem;
                vertical-align: middle;
            }

            #leftContent {
                --btn-padding-x: .25rem;
            }

            .divider {
                width: 1px;
                height: 1.5rem;
                margin: 0 1rem;
                background: var(--main-moon-color);
            }

            .backpackButton {
                display: flex;
                flex-direction: row;
                align-items: center;
            }

            .backpackButton:hover {
                cursor: pointer;
            }


            .buttonSection {
                margin: 0.1rem 0.25rem;
            }

            .grantSection {
                margin: 0.25rem 1rem;
                flex-wrap: nowrap;
            }

            #signinModalComponent {
                --mdc-theme-surface: var(--theme-white-color);
            }

            .linkButton {
                cursor: pointer;
                color: var(--main-primary-color);
                margin: 0 1rem;
            }

            .leftIcon {
                margin-right: 2px;
                --mdc-icon-size: 1rem;
            }

            .leftPng {
                height: 1.5rem;
                margin-right: 5px;
            }

            .backpackicon {
                height: 1rem;
                margin-right: 5px;
            }

            mwc-icon-button {
                color: var(--main-primary-color);
                --mdc-icon-button-size: 2.5rem;
            }

            .mdc-icon-button {
                margin-right: 4px;
            }

            .flexContainer {
                display: flex;
                align-items: center;
            }

            .homeLink {
                margin-right: 1rem;
                color: var(--main-primary-color)
            }
            .homeLink:visited {
                color: var(--main-primary-color);
            }

            .subscriptionTag {
                padding: 1px;
                border-radius: 8px;
                margin-right: 0.5rem;
                display: flex;
                align-items: center;
                cursor: pointer;
                padding: 5px;
            }

            .subscriptionNameTag {
                border-radius: 6px;
                padding: 3px 6px;
                margin-left: 4px;
                margin-right: 4px;
            }

            .spin {
                animation: spin 2s linear infinite;
            }

            @keyframes spin {
                from {
                    transform: rotate(0deg);
                }
                to {
                    transform: rotate(360deg);
                }
            }

            @media screen and (min-width: 901px) {
                .desktopOnly {
                    display: block;
                }

                .mobileOnly {
                    display: none;
                }

                .desktopOnly.flexContainer {
                    display: flex;
                }

                #shareSection {
                    margin-right: -0.5rem;
                }
            }

            @media screen and (max-width: 900px) {
                .desktopOnly {
                    display: none;
                }

                .mobileOnly {
                    display: flex;
                }

                .mobileOnly.flexContainer {
                    display: flex;
                }

                nav {
                    padding: 0 1rem 0 0.75rem;
                }

                .mobileStack {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                }

                .grantSection {
                    margin-right: 0.5rem;
                }

                .pageName {
                    font-size: 12px;
                    line-height: 0.75rem;
                    margin: 5px;
                }

                #rightContent {
                    right: 0px;
                    position: absolute;
                }

                .leftPng {
                    height: 1rem;
                }

                .homeLink {
                    margin-right: 0.5rem;
                }

                .subscriptionTag {
                    padding: 2px;
                    border-radius: 8px;
                    font-size: 12px;
                    margin: 0px
                }

                .subscriptionNameTag {
                    padding: 0px;
                }

                @media screen and (max-width: 600px) {
                    .pageName {
                        display: none;
                    }
                }
            }
        `;
    }

    private async copyCollaboratorMapLinkToClipboard() {
        if (topicData.topic?.is_addpointer === true) {
            if (topicData.discoveryLevel === 'addpoint_public') {
                this.crowdsourceLink = undefinedIfEmpty(topicData.swapUrlForOrgUrl(topicData.topic?.cc_keyless_link ?? ''));
            } else {
                this.crowdsourceLink = undefinedIfEmpty(topicData.swapUrlForOrgUrl(topicData.topic?.cc_addpoint_link ?? ''));
            }
        }
    }

    private async copyViewOnlyMapLinkToClipboard() {
        if (topicData.discoveryLevel !== 'private') {
            this.viewonlyLink = undefinedIfEmpty(topicData.swapUrlForOrgUrl(topicData.topic?.cc_keyless_link ?? ''));
        } else {
            this.viewonlyLink = undefinedIfEmpty(topicData.swapUrlForOrgUrl(topicData.topic?.cc_read_link ?? ''));
        }
    }

    private async duplicateMapClick() {
        logWithAmplitude(Navbar.amplitude_callsite, 'Duplicate Map Click');

        if (detectMobileViewport()) {
            await this.closeOpenDrawers();
            await this.duplicateDrawer?.open();
        } else if (this.duplicationModal) {
            this.duplicationModal.open = true;
        }
    }

    private openUpgradeDialog() {
        this.upgradeDialogComponent.open = true;
        this.shareModal?.close();
    }

    private async shareMapClick() {
        logWithAmplitude(Navbar.amplitude_callsite, 'Share Map Click');

        await this.copyViewOnlyMapLinkToClipboard();
        await this.copyCollaboratorMapLinkToClipboard();

        this.dispatchEvent(new CustomEvent('on-share-map-click', {
            detail: {
                crowdsourceLink: this.crowdsourceLink,
                viewonlyLink: this.viewonlyLink,
            },
        }));
    }

    private async grantAccessClick() {
        logWithAmplitude(Navbar.amplitude_callsite, 'Grant Access Click');
        if (this.type === 'admin-portal') {
            await topicData.mapComponent?.manageAccessClick();
        } else {
            window.location.assign('/#home?page_name=Team%20Settings&setting_type=ManageTeamMembers');
        }
    }

    private signInClick() {
        logWithAmplitude(Navbar.amplitude_callsite, 'User Login Click');
        if (this.signinModalComponent) {
            this.signinModalComponent.open = true;
        }
    }

    private proxiBackpackClick() {
        logWithAmplitude(Navbar.amplitude_callsite, 'Proxi Backpack Click');
        window.location.assign(`/#home`);
    }

    private accountPageClick() {
        logWithAmplitude(Navbar.amplitude_callsite, 'Account Page Click');
        window.location.assign(`/#account`);
    }

    private startMapClick() {
        window.location.assign('/#template-onboard?from=navbar');
    }

    private discoverClick() {
        logWithAmplitude(Navbar.amplitude_callsite, 'Proxi Discover Click');
        window.location.assign(`${getNewUiUrl()}/discover`);
    }

    private demoClick() {
        logWithAmplitude(Navbar.amplitude_callsite, 'Demo Video Click');
        const link = 'https://www.proxi.co/proxi-demo-video';
        window.open(link, '_blank');
    }
}
